/**
 * Which engraved page is on the desk, and turning to the next one.
 *
 * A score is read a page at a time, the way a printed part is: the desk holds one sheet and
 * the pager turns it. Drawing all of them one under the other made a long score thirty pages
 * of scrolling between the reader and the transport.
 *
 * The page turns itself when something being followed -- the bar the music is in, the bar
 * the caret is in -- reaches another page. It turns when that bar's *page changes*, not
 * whenever the bar is off the desk: a reader who turns away to look at another page while
 * the music plays is left there until the music reaches a new page, and is not pulled back
 * on every frame.
 */

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { MeasureId, Moment, Rendering, Score } from "@app/score-editor";
import { scrollerOf } from "./use-playhead";

export interface ShownPage {
  /** 1-based, as the engine numbers pages; 1 when nothing is engraved yet. */
  readonly page: number;
  /** How many pages the engraving has. */
  readonly count: number;
  /** Turn to a page; anything out of range is taken as the nearest end. */
  turn(page: number): void;
}

/** The bar a playhead moment is in, or `null` when there is no moment. */
export function barOf(score: Score, at: Moment | null): MeasureId | null {
  return at === null ? null : (score.measures[at.index]?.id ?? null);
}

/** Clamp a page number into `1..count`. */
function within(page: number, count: number): number {
  return Math.min(Math.max(Math.round(page), 1), Math.max(count, 1));
}

/**
 * The page on the desk of `rendering`, turned to whichever of `following` last moved onto
 * another page. A `null` in `following` is something that is not there to follow just now.
 */
export function useShownPage(
  rendering: Rendering | null,
  following: readonly (MeasureId | null)[],
): ShownPage {
  const count = rendering?.pages.length ?? 0;
  const [chosen, setChosen] = useState(1);
  const seen = useRef<readonly (number | undefined)[]>([]);

  const targets = following.map((id) =>
    id === null || rendering === null ? undefined : rendering.measurePage.get(id),
  );

  // Before paint, so the sheet the music has left is never drawn for a frame. It runs after
  // every render and compares with what it saw last, which is what makes a turn an event.
  useLayoutEffect(() => {
    const was = seen.current;
    seen.current = targets;
    const moved = targets.find((to, at) => to !== undefined && to !== was[at]);
    if (moved !== undefined) setChosen(moved);
  });

  const turn = useCallback((page: number) => setChosen(within(page, count)), [count]);

  return { page: within(chosen, count), count, turn };
}

/** Room left above a page the desk turns to, in CSS pixels. */
const LEAD = 24;

/**
 * Show the top of a newly turned page drawn inside `frame`.
 *
 * The frame is scrolled back to its top, and a scroller around it is moved only if the
 * frame's top has gone above the view. Declare it before `useFollowPlayhead`, so that while
 * the music plays the line's system is brought into view after, and wins.
 */
export function useTopOnTurn(frame: RefObject<HTMLElement>, page: number): void {
  const was = useRef(page);

  useLayoutEffect(() => {
    if (was.current === page) return;
    was.current = page;
    const root = frame.current;
    if (root === null) return;
    root.scrollTop = 0;
    const outer = root.parentElement === null ? null : scrollerOf(root.parentElement);
    if (outer === null) return;
    const top = root.getBoundingClientRect().top;
    const view = outer.getBoundingClientRect().top;
    if (top < view) outer.scrollTop += top - view - LEAD;
  }, [frame, page]);
}
